import { Link } from "react-router-dom";
import {
  ArrowRight,
  Truck,
  ShieldCheck,
  Package,
  Lock,
  CreditCard,
  Boxes,
  MapPin,
  Tag,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à Villeneuve-la-Garenne ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison directe
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Livraison de vos fournitures de déménagement à {city.name}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Acheminés au cœur du tissu urbain, vos équipements de
            préparation font l&apos;objet d&apos;un service de livraison à
            domicile strict et sans faille. Nous éliminons les temps
            d&apos;attente et les ruptures de charge.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            {
              title: "Distribution directe propriétaire",
              body: "Notre propre flotte d'utilitaires franciliens gère l'intégralité du transit, sans jamais sous-traiter à des réseaux de messagerie externes. Cette maîtrise absolue garantit le respect rigoureux de la plage horaire convenue et une manipulation méticuleuse de vos liasses de cartons.",
              Icon: Truck,
            },
            {
              title: "Intégrité et étanchéité",
              body: "Vos fournitures voyagent au sec, isolées des intempéries et des poussières de la route par des parois rigides. Vous réceptionnez un matériel aux propriétés mécaniques intactes, prêt pour un montage structurel immédiat.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Estimation instantanée : 3 étapes ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Devis en temps réel
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Estimation instantanée : configurez vos volumes en temps réel
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Finis les formulaires interminables et les attentes de
            secrétariat. Notre interface a été pensée pour s&apos;aligner
            sur votre emploi du temps et vous délivrer un chiffrage précis
            de manière immédiate.
          </p>
        </div>

        <ol className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              n: "01",
              title: "Ajustement des quantités",
              body: "Parcourez notre catalogue et intégrez directement à votre panier virtuel les modules requis (formats livres, penderies mobiles, rouleaux de blindage alvéolé) ou sélectionnez nos configurations d'espace clés en main.",
              Icon: Boxes,
            },
            {
              n: "02",
              title: "Validation de la zone de dépose",
              body: `Précisez votre secteur à ${city.name} pour que notre système calibre automatiquement l'itinéraire de livraison le plus performant.`,
              Icon: MapPin,
            },
            {
              n: "03",
              title: "Édition du bon de commande",
              body: "En un instant, l'algorithme génère un devis ferme, transparent et détaillé. Vous visualisez instantanément le coût global de vos fournitures, sans aucune ligne de frais cachés ni mauvaise surprise.",
              Icon: Tag,
            },
          ].map(({ n, title, body, Icon }) => (
            <li
              key={n}
              className="relative bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="absolute -top-4 left-5 inline-flex items-center justify-center h-9 px-3 rounded-full bg-[#237cb5] text-white font-display font-extrabold text-[13px] tracking-[0.1em]">
                ÉTAPE {n}
              </div>
              <div className="mt-3 flex items-center gap-2.5">
                <span className="flex items-center justify-center h-9 w-9 rounded-full bg-white border border-[#cfdbe6] text-[#237cb5]">
                  <Icon className="h-4 w-4" strokeWidth={2.6} />
                </span>
                <h3 className="font-display font-extrabold text-[16px] text-[#0f1d2b]">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-10">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-villeneuve-cta-shop"
          >
            Configurer mon devis
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-white">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Paiement fiable 100% (section sombre) ======= */}
    <section className="py-14 md:py-20 bg-[#0f1d2b] text-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-[11px] uppercase tracking-[0.18em] font-bold text-white/80 mb-4">
              <Lock className="h-3.5 w-3.5" strokeWidth={2.6} />
              Sécurité bancaire
            </div>
            <h2 className="font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px]">
              Un protocole de paiement fiable à 100&nbsp;%
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-white/80 leading-relaxed">
              La sécurisation de votre démarche logistique intègre bien
              évidemment la confidentialité de vos données bancaires. Notre
              boutique en ligne déploie les dernières technologies de
              chiffrement pour vous garantir des transactions totalement
              hermétiques.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {[
              {
                title: "Cryptage SSL / TLS de niveau bancaire",
                body: "Toutes les données saisies lors de la finalisation de vos achats sont immédiatement rendues illisibles par nos protocoles de sécurité, empêchant toute interception externe.",
                Icon: Lock,
              },
              {
                title: "Dispositif d'authentification forte (3D Secure)",
                body: "Chaque règlement déclenche une vérification obligatoire en lien avec votre application bancaire personnelle. Vous validez l'opération en toute sérénité, l'esprit totalement libre pour vous concentrer sur le tri et le colisage de vos pièces.",
                Icon: CreditCard,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/15 p-5"
              >
                <div className="flex items-start gap-3">
                  <span className="flex items-center justify-center h-10 w-10 rounded-full bg-[#FF7A00] text-white shrink-0">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[14.5px] text-white">
                      {title}
                    </h3>
                    <p className="mt-2 text-[13.5px] text-white/70 leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-villeneuve-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
